/**
 * PreviewToolbar — the floating, bottom-centered preview control bar (Phase 9.9).
 * Refresh · Desktop · Mobile · View Live. App chrome only (Tailwind-styled); it never
 * enters the generated site. No Phase-10 editing controls.
 */
import { motion } from 'framer-motion'
import { ExternalLink, Monitor, RotateCw, Smartphone } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type PreviewMode = 'desktop' | 'mobile'

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  active,
}: {
  readonly icon: LucideIcon
  readonly label: string
  readonly onClick: () => void
  readonly active?: boolean
}) {
  const base = 'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-150'
  const state = active
    ? 'bg-gray-900 text-white shadow-sm'
    : 'text-gray-500 hover:-translate-y-0.5 hover:bg-gray-100 hover:text-gray-900'
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={`${base} ${state}`}
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}

export function PreviewToolbar({
  mode,
  onMode,
  onRefresh,
  onViewLive,
}: {
  readonly mode: PreviewMode
  readonly onMode: (mode: PreviewMode) => void
  readonly onRefresh: () => void
  readonly onViewLive: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="fixed bottom-5 left-1/2 z-[60] -translate-x-1/2"
    >
      <div
        role="toolbar"
        aria-label="Preview controls"
        className="flex items-center gap-1 rounded-full border border-gray-200 bg-white/90 p-1.5 shadow-xl ring-1 ring-black/5 backdrop-blur-md"
      >
        <ToolbarButton icon={RotateCw} label="Reload preview" onClick={onRefresh} />
        <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
        <ToolbarButton icon={Monitor} label="Desktop preview" active={mode === 'desktop'} onClick={() => onMode('desktop')} />
        <ToolbarButton icon={Smartphone} label="Mobile preview" active={mode === 'mobile'} onClick={() => onMode('mobile')} />
        <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
        <ToolbarButton icon={ExternalLink} label="View live in new tab" onClick={onViewLive} />
      </div>
    </motion.div>
  )
}
